import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { promotionAPI, categoryAPI } from '../services/api.js';
import Navbar from '../components/Navbar.jsx';
import styles from './Home.module.css';

const Home = () => {
  const [heroPromotions, setHeroPromotions] = useState([]);
  const [categories, setCategories] = useState([]);
  const [featuredProducts, setFeaturedProducts] = useState([]);
  const [topPicks, setTopPicks] = useState([]);
  const [randomProducts, setRandomProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activePromoIndex, setActivePromoIndex] = useState(0);
  const [timeLeft, setTimeLeft] = useState({});
  const [debugMode] = useState(false); // Set to true for debugging

  const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

  // Category themes matching your category page
  const categoryThemes = {
    'agricultural-produce': {
      slug: 'agricultural-produce',
      displayName: 'Agricultural Produce',
      color: '#FF9800',
      gradient: 'linear-gradient(135deg, #FF9800 0%, #F57C00 100%)',
      icon: '🌱'
    },
    'beauty': {
      slug: 'beauty',
      displayName: 'Beauty & Cosmetics',
      color: '#9C27B0',
      gradient: 'linear-gradient(135deg, #9C27B0 0%, #7B1FA2 100%)',
      icon: '💄'
    },
    'fashion': {
      slug: 'fashion',
      displayName: 'Fashion & Apparel',
      color: '#FF6B8B',
      gradient: 'linear-gradient(135deg, #FF6B8B 0%, #FF5A6E 100%)',
      icon: '👗'
    },
    'household': {
      slug: 'household',
      displayName: 'Home & Living',
      color: '#009688',
      gradient: 'linear-gradient(135deg, #009688 0%, #00796B 100%)',
      icon: '🏠'
    }
  };

  // Fetch products from API (similar to ProductCatalog)
  const fetchProducts = async (options = {}) => {
    try {
      const params = new URLSearchParams();
      
      // Add sort option
      if (options.sort) {
        params.append('sort', options.sort);
      }
      
      // Add limit
      if (options.limit) {
        params.append('limit', options.limit);
      }
      
      // Add filter for featured products
      if (options.filterFeatured) {
        params.append('isFeatured', 'true');
      }

      const queryString = params.toString();
      const url = `${API_URL}/products?${queryString}`;

      if (debugMode) {
        console.log('Fetching products from:', url);
      }
      
      const response = await fetch(url);
      
      if (!response.ok) {
        throw new Error(`Failed to fetch products: ${response.status}`);
      }
      
      const data = await response.json();
      let productsData = data.products || data || [];
      
      if (debugMode) {
        console.log('Fetched products:', productsData.length);
        
        // Debug: Log inventory data for all products
        if (debugMode) {
          console.log('🧪 INVENTORY DEBUG FOR HOME PRODUCTS:');
          productsData.forEach(product => {
            const availableStock = product.inventory ? 
              (product.inventory.stock - (product.inventory.reservedStock || 0)) : 0;
            
            console.log(`📦 ${product.name}:`, {
              stock: product.inventory?.stock,
              reserved: product.inventory?.reservedStock,
              available: availableStock
            });
          });
        }
      }
      
      return productsData;
      
    } catch (error) {
      console.error('Error fetching products:', error);
      return [];
    }
  };

  // Fetch data with fallbacks
  useEffect(() => {
    const fetchHomeData = async () => {
      try {
        setLoading(true);
        let allProducts = [];
        
        // 1. Try to fetch real products from API
        console.log('📦 Fetching products from API for Home page...');
        allProducts = await fetchProducts({ limit: 50 });
        
        if (allProducts.length === 0) {
          console.log('⚠️ No products from API, using API endpoint directly');
          
          // Try direct API call as fallback
          try {
            const directResponse = await fetch(`${API_URL}/products`);
            if (directResponse.ok) {
              const data = await directResponse.json();
              allProducts = data.products || data || [];
            }
          } catch (directError) {
            console.error('❌ Direct API call failed:', directError);
          }
        }

        // 2. Process products for different sections
        if (allProducts.length > 0) {
          console.log('✅ Products loaded:', allProducts.length);
          
          // Get TOP PICKS - Sort by sales count first, then views, then created date
          const topPicksSorted = [...allProducts]
            .sort((a, b) => {
              // Prioritize by sales count
              const aSales = a.salesCount || 0;
              const bSales = b.salesCount || 0;
              if (bSales !== aSales) return bSales - aSales;
              
              // Then by views
              const aViews = a.views || 0;
              const bViews = b.views || 0;
              if (bViews !== aViews) return bViews - aViews;
              
              // Then by flags (featured, bestseller)
              const aScore = (a.flags?.isFeatured ? 10 : 0) + 
                           (a.flags?.isBestSeller ? 5 : 0) + 
                           (a.flags?.isNew ? 3 : 0);
              const bScore = (b.flags?.isFeatured ? 10 : 0) + 
                           (b.flags?.isBestSeller ? 5 : 0) + 
                           (b.flags?.isNew ? 3 : 0);
              if (bScore !== aScore) return bScore - aScore;
              
              // Finally by creation date (newest first)
              const aDate = new Date(a.createdAt || 0);
              const bDate = new Date(b.createdAt || 0);
              return bDate - aDate;
            })
            .slice(0, 6);
          setTopPicks(topPicksSorted);
          
          // Get FEATURED products
          const featured = allProducts
            .filter(p => p.flags?.isFeatured)
            .slice(0, 3);
          
          // If no featured products, take from top picks
          if (featured.length === 0) {
            setFeaturedProducts(topPicksSorted.slice(0, 3));
          } else {
            setFeaturedProducts(featured);
          }
          
          // Get RANDOM products
          const random = [...allProducts]
            .sort(() => Math.random() - 0.5)
            .slice(0, 6);
          setRandomProducts(random);
        } else {
          console.log('🚨 No products found in database');
        }

        // 3. Fetch PROMOTIONS (keep your existing logic)
        try {
          console.log('🚀 Fetching hero promotions...');
          const response = await promotionAPI.getActive('hero');
          
          if (response.success && response.data && response.data.length > 0) {
            const promotions = response.data;
            const heroPromos = promotions.filter(promo => 
              promo.position === 'hero' || 
              promo.position === 'top_banner' ||
              !promo.position
            ).slice(0, 3);
            
            setHeroPromotions(heroPromos);
            
            // Calculate time left
            const times = {};
            heroPromos.forEach(promo => {
              if (promo.endDate) {
                const end = new Date(promo.endDate);
                const now = new Date();
                const diff = end - now;
                
                if (diff > 0) {
                  const days = Math.floor(diff / (1000 * 60 * 60 * 24));
                  const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
                  const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
                  times[promo._id] = { days, hours, minutes };
                }
              }
            });
            setTimeLeft(times);
          } else {
            // Fallback promotion
            setHeroPromotions([
              {
                _id: 'fallback-1',
                title: 'Welcome to Bloom Soko!',
                description: 'Discover premium products from trusted local producers',
                type: 'special_offer',
                targetUrl: '/products',
                backgroundColor: '#4CAF50',
                textColor: '#FFFFFF',
                bannerImage: { 
                  url: 'https://images.unsplash.com/photo-1441986300917-64674bd600d8?w=800&h=400&fit=crop', 
                  alt: 'Welcome to Bloom Soko' 
                },
                endDate: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString(),
                discountValue: 10,
                discountType: 'percentage'
              }
            ]);
          }
        } catch (promoError) {
          console.error('❌ Failed to fetch promotions:', promoError);
          // Set fallback promotion
          setHeroPromotions([
            {
              _id: 'fallback-2',
              title: 'Shop Local, Shop Fresh',
              description: 'Support local producers with premium quality products',
              type: 'flash_sale',
              targetUrl: '/products',
              backgroundColor: '#FF9800',
              textColor: '#FFFFFF'
            }
          ]);
        }

        // 4. Fetch CATEGORIES (keep your existing logic)
        try {
          const response = await categoryAPI.getMain();
          
          if (response.success && response.data) {
            const mappedCategories = response.data.map(cat => {
              const slug = cat.name.toLowerCase().replace(/\s+/g, '-');
              const theme = categoryThemes[slug] || {
                slug: slug,
                displayName: cat.name,
                color: '#4CAF50',
                gradient: 'linear-gradient(135deg, #4CAF50, #2E7D32)',
                icon: '🛍️'
              };
              return {
                ...cat,
                slug: slug,
                theme: theme
              };
            }).slice(0, 6);
            
            setCategories(mappedCategories);
          } else {
            // Fallback categories from API endpoint
            try {
              const directCatResponse = await fetch(`${API_URL}/categories`);
              if (directCatResponse.ok) {
                const data = await directCatResponse.json();
                let categoriesArray = [];
                
                if (Array.isArray(data)) {
                  categoriesArray = data;
                } else if (data && Array.isArray(data.categories)) {
                  categoriesArray = data.categories;
                } else if (data && Array.isArray(data.data)) {
                  categoriesArray = data.data;
                }
                
                const mainCategories = categoriesArray
                  .filter(cat => cat && cat.name)
                  .filter(cat => cat.level === 1 || !cat.level)
                  .slice(0, 6)
                  .map(cat => {
                    const slug = cat.name.toLowerCase().replace(/\s+/g, '-');
                    const theme = categoryThemes[slug] || {
                      slug: slug,
                      displayName: cat.name,
                      color: '#4CAF50',
                      gradient: 'linear-gradient(135deg, #4CAF50, #2E7D32)',
                      icon: '🛍️'
                    };
                    return {
                      ...cat,
                      slug: slug,
                      theme: theme
                    };
                  });
                
                setCategories(mainCategories);
              }
            } catch (catError) {
              console.error('Direct categories fetch failed:', catError);
              setCategories(Object.values(categoryThemes));
            }
          }
        } catch (categoryError) {
          console.error('❌ Failed to fetch categories:', categoryError);
          setCategories(Object.values(categoryThemes));
        }

      } catch (error) {
        console.error('❌ General error in fetchHomeData:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchHomeData();
  }, []);

  // Auto-rotate promotions
  useEffect(() => {
    if (heroPromotions.length > 1) {
      const interval = setInterval(() => {
        setActivePromoIndex(prev => (prev + 1) % heroPromotions.length);
      }, 5000);
      return () => clearInterval(interval);
    }
  }, [heroPromotions]);

  if (loading) {
    return (
      <div className={styles.loadingContainer}>
        <div className={styles.loadingSpinner}></div>
        <div>Loading Bloom Soko...</div>
      </div>
    );
  }

  const activePromotion = heroPromotions[activePromoIndex];

  return (
    <div className={styles.homePage}>
      <Navbar />
      
      {/* 🌟 HERO SECTION WITH PROMOTIONS */}
      <section className={styles.heroSection}>
        <div className={styles.heroContent}>
          {/* Left side: Text content */}
          <div className={styles.heroText}>
            
            
            <h1 className={styles.heroTitle}>
              Discover <span className={styles.highlight}>Premium</span> Products<br />
              From <span className={styles.highlight}>Trusted</span> Producers
            </h1>
            
            <p className={styles.heroDescription}>
              Your one-stop marketplace for agricultural produce, beauty essentials, 
              fashion items, and household goods. Direct from source to your doorstep.
            </p>
            
            <div className={styles.heroActions}>
              <Link to="/products" className={styles.primaryButton}>
                🛍️ Start Shopping
              </Link>
          
            </div>
          </div>
          
          {/* Right side: PROMOTIONS CAROUSEL */}
          <div className={styles.promotionSection}>
            <div className={styles.promotionHeader}>
              <h3>🔥 Hot Deals</h3>
              {heroPromotions.length > 1 && (
                <div className={styles.promoIndicators}>
                  {heroPromotions.map((_, index) => (
                    <button
                      key={index}
                      className={`${styles.indicator} ${index === activePromoIndex ? styles.active : ''}`}
                      onClick={() => setActivePromoIndex(index)}
                    />
                  ))}
                </div>
              )}
            </div>
            
            <div 
              className={styles.activePromotion}
              style={{ 
                background: activePromotion?.backgroundColor || 
                (activePromotion?.type === 'flash_sale' ? '#FF4081' : 
                 activePromotion?.type === 'discount' ? '#4CAF50' : 
                 activePromotion?.type === 'new_arrival' ? '#2196F3' : '#4CAF50') 
              }}
            >
              {/* Promotion Banner Image */}
              <div className={styles.promoBanner}>
                {activePromotion?.bannerImage?.url ? (
                  <img 
                    src={activePromotion.bannerImage.url} 
                    alt={activePromotion.bannerImage.alt || activePromotion.title}
                    className={styles.promoImage}
                  />
                ) : (
                  <div className={styles.promoImageFallback}>
                    {activePromotion?.type === 'flash_sale' ? '🔥' : 
                     activePromotion?.type === 'discount' ? '🏷️' : 
                     activePromotion?.type === 'free_delivery' ? '🚚' : '🎉'}
                  </div>
                )}
              </div>
              
              <div className={styles.promoContent}>
                <div className={styles.promoHeaderRow}>
                  <div className={styles.promoType}>
                    {activePromotion?.type ? 
                      activePromotion.type.replace('_', ' ').toUpperCase() : 
                      'SPECIAL OFFER'}
                  </div>
                  
                  {activePromotion?.discountValue && (
                    <div className={styles.discountBadge}>
                      {activePromotion.discountType === 'percentage' 
                        ? `${activePromotion.discountValue}% OFF` 
                        : `SAVE KSh ${activePromotion.discountValue}`}
                    </div>
                  )}
                </div>
                
                <h4 className={styles.promoTitle}>
                  {activePromotion?.title || 'Welcome to Bloom Soko'}
                </h4>
                
                <p className={styles.promoDescription}>
                  {activePromotion?.description || 
                   'Discover amazing products at great prices. Limited time offer!'}
                </p>
                
                <Link 
                  to={activePromotion?.targetUrl || '/products'}
                  className={styles.promoButton}
                >
                  Shop Now →
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 📂 CATEGORIES SECTION */}
      <section className={styles.categoriesSection}>
        <div className={styles.sectionHeader}>
          <h2>Browse Categories</h2>
          <p>Shop from our premium collections</p>
        </div>
        
        {categories.length > 0 ? (
          <div className={styles.categoriesGrid}>
            {categories.map(category => (
              <Link 
                key={category.slug || category._id} 
                to={`/categories/${category.slug}`}
                className={styles.categoryCard}
                style={{ 
                  borderColor: category.color || category.theme?.color || '#4CAF50'
                }}
              >
                <div 
                  className={styles.categoryIcon}
                  style={{ 
                    background: category.color || category.theme?.color || '#4CAF50',
                    color: 'white'
                  }}
                >
                  {category.icon || category.theme?.icon || '🛍️'}
                </div>
                <h3>{category.displayName || category.name}</h3>
                <p className={styles.categoryDescription}>
                  {category.description || `Explore ${category.displayName || category.name} products`}
                </p>
                <span className={styles.categoryLink}>
                  Shop Now →
                </span>
              </Link>
            ))}
          </div>
        ) : (
          <div className={styles.noCategories}>
            <p>No categories available yet. Check back soon!</p>
          </div>
        )}
      </section>

      {/* 🏆 TOP PICKS SECTION - REAL PRODUCTS FROM API */}
      <section className={styles.topPicksSection}>
        <div className={styles.sectionHeader}>
          <h2>Top Picks This Week</h2>
          <p>Best selling products loved by our customers</p>
        </div>
        
        {topPicks.length > 0 ? (
          <>
            <div className={styles.productsGrid}>
              {topPicks.map((product, index) => (
                <ProductCard key={product._id || product.name + index} product={product} index={index} />
              ))}
            </div>
            
            <div className={styles.sectionFooter}>
              <Link to="/products?sort=trending" className={styles.viewAllBtn}>
                View All Top Picks →
              </Link>
            </div>
          </>
        ) : (
          <div className={styles.noProducts}>
            <p>No products available yet. Products will appear here once added to the system.</p>
            <Link to="/products" className={styles.ctaButton}>
              Browse All Products →
            </Link>
          </div>
        )}
      </section>

      {/* ⭐ FEATURED PRODUCTS */}
      {featuredProducts.length > 0 && (
        <section className={styles.featuredSection}>
          <div className={styles.sectionHeader}>
            <h2>Featured Products</h2>
            <p>Handpicked items just for you</p>
          </div>
          <div className={styles.productsGrid}>
            {featuredProducts.map(product => (
              <ProductCard key={product._id} product={product} />
            ))}
          </div>
          <div className={styles.sectionFooter}>
            <Link to="/products?filter=featured" className={styles.viewAllBtn}>
              View All Featured →
            </Link>
          </div>
        </section>
      )}

      {/* 🎲 RANDOM PRODUCTS */}
      {randomProducts.length > 0 && (
        <section className={styles.randomSection}>
          <div className={styles.sectionHeader}>
            <h2>Discover Something New</h2>
            <p>Fresh picks updated on every visit</p>
          </div>
          <div className={styles.productsGrid}>
            {randomProducts.map(product => (
              <ProductCard key={product._id} product={product} />
            ))}
          </div>
          <div className={styles.randomNote}>
            <span className={styles.refreshIcon}>🔄</span>
          
          </div>
        </section>
      )}

      {/* 🎯 CALL TO ACTION */}
      <section className={styles.ctaSection}>
        <div className={styles.ctaContent}>
          <h2>Ready to Shop?</h2>
          <p>Join thousands of happy customers shopping with Bloom Soko</p>
          <Link to="/products" className={styles.ctaButton}>
            Start Shopping Now →
          </Link>
        </div>
      </section>

      
      {/* 👣 FOOTER */}
<footer className={styles.footer}>
  <div className={styles.footerContent}>
    {/* Brand Section */}
    <div className={styles.footerBrand}>
      <div className={styles.brandHeader}>
        <span className={styles.logoIcon}></span>
        <h3 className={styles.brandName}>Bloom Soko</h3>
      </div>
      <p className={styles.brandTagline}>Connecting growers with buyers since 2024</p>
      
      {/* Social Links */}
      <div className={styles.socialLinks}>
        <a href="https://facebook.com" target="_blank" rel="noopener noreferrer" className={styles.socialLink}>
          <span className={styles.socialIcon}>📘</span>
          <span className={styles.socialText}>Facebook</span>
        </a>
        <a href="https://instagram.com" target="_blank" rel="noopener noreferrer" className={styles.socialLink}>
          <span className={styles.socialIcon}>📷</span>
          <span className={styles.socialText}>Instagram</span>
        </a>
        <a href="https://twitter.com" target="_blank" rel="noopener noreferrer" className={styles.socialLink}>
          <span className={styles.socialIcon}>🐦</span>
          <span className={styles.socialText}>Twitter</span>
        </a>
        <a href="https://linkedin.com" target="_blank" rel="noopener noreferrer" className={styles.socialLink}>
          <span className={styles.socialIcon}>💼</span>
          <span className={styles.socialText}>LinkedIn</span>
        </a>
      </div>
    </div>
    
    {/* Shop Links */}
    <div className={styles.footerLinks}>
      <div className={styles.linkColumn}>
        <h4 className={styles.columnTitle}>Shop</h4>
        <Link to="/products" className={styles.footerLink}>All Products</Link>
        <Link to="/categories" className={styles.footerLink}>Categories</Link>
        <Link to="/products?sort=featured" className={styles.footerLink}>Featured</Link>
        <Link to="/products?sort=bestSelling" className={styles.footerLink}>Top Picks</Link>
      </div>
    </div>
  </div>
  
  {/* Bottom Bar */}
  <div className={styles.footerBottom}>
    <div className={styles.bottomContent}>
      <div className={styles.copyright}>
        <p>© 2026 Bloom Soko. All rights reserved.</p>
      </div>
      <div className={styles.contactInfo}>
        <span className={styles.contactItem}>📍 Kenya</span>
        <span className={styles.divider}>|</span>
        <a href="tel:+254708756517" className={styles.contactItem}>📞 +254 708 756 517</a>
        <span className={styles.divider}>|</span>
        <a href="mailto:bloomsoko@gmail.com" className={styles.contactItem}>✉️ bloomsoko@gmail.com</a>
      </div>
    </div>
  </div>
</footer>
    </div>
  );
};

// Updated Product Card Component with real product data
const ProductCard = ({ product, index }) => {
  const imageUrl = product.featuredImage?.url || product.images?.[0]?.url;
  const isReady = product.productType === 'ready';
  const availableStock = product.inventory ? 
    (product.inventory.stock - (product.inventory.reservedStock || 0)) : 0;
  
  // Get category color - matches your ProductCatalog logic
  const getCategoryColor = () => {
    if (!product.category) return '#4CAF50';
    
    const categoryName = typeof product.category === 'string' 
      ? product.category 
      : product.category.name || '';
    
    const categoryMap = {
      'Agricultural Produce': '#FF9800',
      'Agriculture': '#FF9800',
      'Beauty': '#9C27B0',
      'Beauty & Cosmetics': '#9C27B0',
      'Fashion': '#FF6B8B',
      'Fashion & Apparel': '#FF6B8B',
      'Household': '#009688',
      'Home & Garden': '#009688',
      'Home & Living': '#009688'
    };
    
    return categoryMap[categoryName] || '#4CAF50';
  };

  const categoryColor = getCategoryColor();

  // Get product badges (similar to ProductCatalog)
  const getProductBadges = () => {
    const badges = [];
    
    if (availableStock <= 0) {
      badges.push({ text: 'Out of Stock', class: 'outOfStock' });
    }
    
    if (product.productType === 'growing') {
      badges.push({ text: '🌱 Growing', class: 'growing' });
    } else if (product.productType === 'pre-order') {
      badges.push({ text: '📅 Pre-order', class: 'preOrder' });
    }
    
    if (product.flags?.isNew) badges.push({ text: '🆕 New', class: 'new' });
    if (product.flags?.isLimited) badges.push({ text: '⏰ Limited', class: 'limited' });
    if (product.flags?.onSale) badges.push({ text: '🏷️ Offer', class: 'sale' });
    if (product.flags?.isBestSeller) badges.push({ text: '🔥 Best Seller', class: 'bestSeller' });
    
    return badges.slice(0, 2);
  };

  const badges = getProductBadges();

  return (
    <Link 
      to={`/product/${product._id}`} 
      className={styles.productCard}
      style={{ borderColor: categoryColor }}
    >
      <div className={styles.productImage}>
        {imageUrl ? (
          <img 
            src={imageUrl} 
            alt={product.name}
            className={styles.productImg}
            onError={(e) => {
              e.target.style.display = 'none';
              e.target.nextElementSibling.style.display = 'flex';
            }}
          />
        ) : null}
        <div className={styles.imageFallback}>
          {product.name?.charAt(0) || 'P'}
        </div>
        
        {/* Badge Container */}
        <div className={styles.badgeContainer}>
          {badges.map((badge, badgeIndex) => (
            <span 
              key={badgeIndex} 
              className={`${styles.productBadge} ${styles[badge.class]}`}
              style={{
                backgroundColor: badge.class === 'sale' ? '#FF4081' :
                               badge.class === 'new' ? '#2196F3' :
                               badge.class === 'bestSeller' ? '#FF9800' :
                               badge.class === 'outOfStock' ? '#757575' :
                               badge.class === 'growing' ? '#4CAF50' : '#9C27B0'
              }}
            >
              {badge.text}
            </span>
          ))}
        </div>
        
        
      </div>
      
      <div className={styles.productInfo}>
        <div 
          className={styles.productCategory}
          style={{ color: categoryColor }}
        >
          {typeof product.category === 'string' 
            ? product.category 
            : product.category?.name || 'Product'}
        </div>
        
        <h3 className={styles.productName}>{product.name}</h3>
        
        <p className={styles.productDescription}>
          {product.description?.substring(0, 60) || 'Premium quality product...'}
        </p>
        
        {/* Price section with discount if applicable */}
        <div className={styles.priceSection}>
          <span 
            className={styles.productPrice}
            style={{ color: categoryColor }}
          >
            KSh {product.price?.toLocaleString() || '0'}
          </span>
          {product.comparePrice && product.comparePrice > product.price && (
            <span className={styles.comparePrice}>
              KSh {product.comparePrice?.toLocaleString()}
            </span>
          )}
          {product.flags?.onSale && product.comparePrice && product.comparePrice > product.price && (
            <span className={styles.discountPercentage}>
              Save {Math.round((1 - product.price / product.comparePrice) * 100)}%
            </span>
          )}
        </div>
        
        {/* Stock status */}
        <div className={styles.productMeta}>
          <span className={`${styles.stockStatus} ${isReady && availableStock > 0 ? styles.inStock : styles.outOfStock}`}>
            {isReady ? (availableStock > 0 ? '📦 In Stock' : '⛔ Out of Stock') : '📅 Pre-order'}
          </span>
          
          {availableStock > 0 && isReady && (
            <span className={styles.stockQuantity}>
              {availableStock} available
            </span>
          )}
        </div>
        
        {/* Quick view link */}
        <div className={styles.quickViewLink}>
          View Details →
        </div>
      </div>
    </Link>
  );
};

export default Home;